import React, { useState, useEffect } from 'react';
import { expensesAPI, casesAPI } from '../services/api';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import { Plus, Search, Edit2, Trash2, DollarSign } from 'lucide-react';
import { toast } from 'react-toastify';

const CATEGORIES = [
  // Case disbursements
  'Filing Fees', 'Travel', 'Research', 'Postage', 'Printing', 'Telephone',
  'Expert Witness', 'Court Reporter', 'Sheriff Fees', 'Stamp Duty',
  // Firm operating expenses
  'Rent', 'Salaries & Wages', 'Utilities', 'Insurance', 'IT & Software',
  'Office Supplies', 'Professional Fees', 'Bank Charges', 'Depreciation',
  'Advertising', 'Other',
];
const CASE_CATEGORIES = new Set([
  'Filing Fees', 'Travel', 'Research', 'Postage', 'Printing', 'Telephone',
  'Expert Witness', 'Court Reporter', 'Sheriff Fees', 'Stamp Duty',
]);
const EMPTY = { case: '', date_incurred: new Date().toISOString().slice(0, 10), amount: '', category: 'Other', description: '', billed_to_client: false };

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (catFilter) params.category = catFilter;
      const { data } = await expensesAPI.list(params);
      setExpenses(data.results || data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); casesAPI.list().then(r => setCases(r.data.results || r.data)); }, []);
  useEffect(() => { load(); }, [catFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        case: form.case || null,
        amount: parseFloat(form.amount),
        billed_to_client: form.billed_to_client === true || form.billed_to_client === 'true',
      };
      if (editing) { await expensesAPI.update(editing.id, payload); toast.success('Expense updated'); }
      else { await expensesAPI.create(payload); toast.success('Expense added'); }
      setModalOpen(false);
      load();
    } catch (err) {
      const detail = err.response?.data;
      toast.error(detail ? JSON.stringify(detail) : 'Failed to save expense');
    }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    await expensesAPI.delete(id);
    toast.success('Deleted');
    load();
  };

  const total = expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
  const unbilled = expenses.filter(e => !e.billed_to_client).reduce((s, e) => s + parseFloat(e.amount || 0), 0);

  const f = (name) => ({ value: form[name] ?? '', onChange: evt => setForm({ ...form, [name]: evt.target.value }) });

  return (
    <div className="p-6">
      <PageHeader
        title="Expenses"
        subtitle="Track case disbursements and firm operating expenses"
        actions={<button className="btn-primary" onClick={() => { setEditing(null); setForm(EMPTY); setModalOpen(true); }}><Plus size={16} />Add Expense</button>}
      />

      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="card text-center">
          <div className="text-2xl font-bold text-gray-900">US${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          <div className="text-xs text-gray-500 mt-1">Total Expenses</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-amber-600">US${unbilled.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          <div className="text-xs text-gray-500 mt-1">Operating / Unbilled</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-blue-700">US${(total - unbilled).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          <div className="text-xs text-gray-500 mt-1">Billed to Clients</div>
        </div>
      </div>

      <div className="flex gap-3 mb-5">
        <select className="input w-48" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>{['Date', 'Case', 'Category', 'Description', 'Amount', 'Billed', 'Actions'].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? <tr><td colSpan={7} className="text-center py-12 text-gray-400">Loading...</td></tr>
              : expenses.length === 0 ? <tr><td colSpan={7} className="text-center py-12 text-gray-400">No expenses yet.</td></tr>
              : expenses.map(e => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-600">{e.date_incurred}</td>
                  <td className="px-4 py-3 text-gray-700">{e.case_title || '—'}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-gray-100 px-2 py-1 rounded">{e.category}</span></td>
                  <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{e.description}</td>
                  <td className="px-4 py-3 font-semibold text-gray-900">${parseFloat(e.amount).toFixed(2)}</td>
                  <td className="px-4 py-3"><StatusBadge status={e.billed_to_client ? 'Billed' : 'Unbilled'} /></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button className="p-1.5 text-gray-400 hover:text-amber-600" onClick={() => { setEditing(e); setForm({ ...e }); setModalOpen(true); }}><Edit2 size={14} /></button>
                      <button className="p-1.5 text-gray-400 hover:text-red-600" onClick={() => handleDelete(e.id)}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Expense' : 'Add Expense'}>
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Category *</label>
            <select className="input" required {...f('category')}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Case <span className="text-gray-400 font-normal">(optional)</span></label>
            <select className="input" value={form.case || ''} onChange={e => setForm({ ...form, case: e.target.value || null })}>
              <option value="">— Firm operating expense —</option>
              {cases.map(c => <option key={c.id} value={c.id}>{c.case_title}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Date *</label>
            <input type="date" className="input" required {...f('date_incurred')} />
          </div>
          <div>
            <label className="label">Amount (US$) *</label>
            <input type="number" step="0.01" min="0.01" className="input" required {...f('amount')} />
          </div>
          <div className="col-span-2">
            <label className="label">Description *</label>
            <textarea className="input" rows={2} required value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <input type="checkbox" id="billed" checked={form.billed_to_client === true || form.billed_to_client === 'true'}
              onChange={e => setForm({ ...form, billed_to_client: e.target.checked })} className="rounded" />
            <label htmlFor="billed" className="text-sm text-gray-700">Bill this expense to the client</label>
          </div>
          {!form.case && (
            <p className="col-span-2 text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
              No case selected — this will be recorded as a firm operating expense.
            </p>
          )}
          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : editing ? 'Update' : 'Add Expense'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
